/**
 * g1xl-v2#1053 — the index cycle a view runs to NAME its rows on an
 * edge-served sport. One state machine, shared by the Value Dashboard board
 * (its line pick, `useBoardFeedNeeds`) and a `/game/*` page (its game's rows,
 * `useRouteSportFeedNeeds`):
 *
 *   owner resolve → REST index → pick → hold / backoff / hidden-defer
 *
 * The caller says what to fetch and what to declare in each state; this
 * module decides when.
 *
 * - Owners come from the store's live ownership: the sport's K/Intl owner and
 *   its separate Poly US owner (a `hepu-*` instance), each keyed `id|role` —
 *   a role change is a new answer, not only an id change. An owner's
 *   `floorSports` never count: a core covering a sport while its edge
 *   restarts is not its owner.
 * - Core- or firehose-served → the caller's sport-wide declaration (`broad`;
 *   the core floor ignores the tickers axis by design). An edge-served sport
 *   is never declared whole.
 * - Owner unknown, nothing to index yet (`ready`), index slow or failing, no
 *   REST origin → MUTED: an empty allow-list on the edge, while the core
 *   floor carries the sport. Provisional only while a first answer is on
 *   the way; "Loading" (`pending`) ends after `INDEX_WAIT_MS`, and the fetch
 *   continues.
 * - A pick is replaced only by a newer pick: owner flaps (an edge restart
 *   drops its announce) and failed refreshes keep it — the US owner's names
 *   too. A failed index retries at 5 s, 15 s, 30 s, then every 60 s; a pick
 *   is re-indexed every `INDEX_REFRESH_MS`.
 * - The scope never changes while the document is hidden: a refresh or an
 *   owner change that comes due while hidden runs on the return. Only the
 *   FIRST pick proceeds (a view opened in a background tab must still
 *   declare something narrow).
 */
import { useMarketStore } from '@/stores/marketStore';
import { engineRestFromWsUrl, type EngineRest } from '@/lib/feedFilter/boardIndex';
import type { FeedVenue } from '@/stores/feedPairOwnership';

/** Re-index cadence for a held pick. */
export const INDEX_REFRESH_MS = 60_000;
/** How long a first answer may take before "Loading" ends (the view stays
 *  muted — never broad — and keeps waiting). */
export const INDEX_WAIT_MS = 4_000;
/** Retry delays after an index failure (or no REST origin); the last one
 *  repeats. A held pick stays declared meanwhile. */
export const INDEX_RETRY_MS = [5_000, 15_000, 30_000, 60_000] as const;
/** The separate US owner's index is an add-on: it may not hold the cycle. */
const US_INDEX_TIMEOUT_MS = 5_000;
const OWNER_POLL_INTERVAL_MS = 2_000;

export interface IndexOwner { id: string; role: 'firehose' | 'core' | 'edge'; url?: string }

/** The feed that owns `sport` (on `venue`, else on any venue) right now, or
 *  null when the store names none. */
export function resolveIndexOwner(sport: string, venue?: FeedVenue): IndexOwner | null {
  const s = useMarketStore.getState() as { getFeedOwnership?: (venue?: FeedVenue) => Array<{ id: string; role: 'firehose' | 'core' | 'edge'; url?: string; sports: string[] }> };
  const ownership = typeof s.getFeedOwnership === 'function' ? s.getFeedOwnership(venue) : null;
  if (!ownership) return null;
  const entry = ownership.find((f) => f.sports.includes(sport));
  return entry ? { id: entry.id, role: entry.role, url: entry.url } : null;
}

/** The owner advertised the proposed line-scope axis (the board's path 1). */
export function ownerAdvertisesLineScope(feedId: string): boolean {
  const s = useMarketStore.getState() as { getFeedFilterAxes?: (id: string) => ReadonlySet<string> };
  return typeof s.getFeedFilterAxes === 'function' && s.getFeedFilterAxes(feedId).has('lineScope');
}

function documentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export type IndexCycleMode = 'off' | 'muted' | 'lineScope' | 'tickers' | 'broad';

export interface IndexCycleState {
  mode: IndexCycleMode;
  owner: string | null;
  /** Names declared (the pick ∪ the US owner's names). */
  tickers: number;
  error: string | null;
  indexedAtMs: number | null;
  /** True until the view knows what it declares (the "Loading" signal). */
  pending: boolean;
}

export interface IndexCycleSpec<P> {
  /** g1 sport id; resolved against the store's ownership. */
  sport: string;
  /** False while there is nothing to index yet (a `/game/*` page whose rows
   *  have not named its game): handled like an unknown owner. */
  ready?: () => boolean;
  /** The owning edge's pick, from its REST index. */
  fetchPick: (rest: EngineRest, signal: AbortSignal) => Promise<P>;
  /** The names a pick declares. */
  pickTickers: (pick: P) => readonly string[];
  /** The separate Poly US owner's names for this pick; absent = not indexed. */
  fetchUsTickers?: (rest: EngineRest, pick: P, signal: AbortSignal) => Promise<readonly string[]>;
  /** Declarations, one per answer. */
  broad: () => void;
  muted: (provisional: boolean) => void;
  tickers: (names: readonly string[]) => void;
  /** The engine picks the lines itself (path 1); absent = never asked. */
  lineScope?: () => void;
  /** Every state change; `pick` rides the change a fresh pick made. */
  onState: (state: Readonly<IndexCycleState>, pick?: P) => void;
}

export interface IndexCycle {
  /** The caller's input moved (a fold, the game): re-declare the current
   *  answer with it, then re-index now. Hidden with an answer held → on the
   *  return. */
  refresh(): void;
  /** Re-declare the current answer (a ladder or a fold changed); no index. */
  redeclare(): void;
  stop(): void;
}

export function startIndexCycle<P>(spec: IndexCycleSpec<P>): IndexCycle {
  const { sport } = spec;
  const state: IndexCycleState = { mode: 'off', owner: null, tickers: 0, error: null, indexedAtMs: null, pending: true };
  const publish = (pick?: P): void => spec.onState({ ...state }, pick);
  let cancelled = false;

  /** A pick (or the engine's own line scope) is declared. */
  const hasPick = (): boolean => state.mode === 'tickers' || state.mode === 'lineScope';
  let mutedProvisional = false;
  const enter = (mode: IndexCycleMode, declare: () => void, pick?: P): void => {
    declare();
    state.mode = mode;
    // A quiet step is still on the way to the answer — unless it IS the
    // answer for now (an error is recorded: the view says what it has).
    state.pending = mode === 'muted' && state.error == null;
    publish(pick);
  };
  /** Quiet, never broad. Provisional only while a first answer is on the way
   *  (the transports keep the previous scope on the wire meanwhile). */
  const mute = (provisional: boolean): void => {
    mutedProvisional = provisional;
    enter('muted', () => spec.muted(provisional));
  };

  let pickNames: readonly string[] = [];
  // The separate US owner's names, held like the pick: replaced only by a
  // newer US answer — an owner flap or a failed US refresh keeps them.
  let usNames: readonly string[] = [];
  const names = (): string[] => [...new Set([...pickNames, ...usNames])].sort();
  const declareNames = (pick?: P): void => {
    const list = names();
    state.tickers = list.length;
    enter('tickers', () => spec.tickers(list), pick);
  };

  let controller: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let wait: ReturnType<typeof setTimeout> | null = null;
  // `id|role` of the K/Intl and US owners the last cycle saw.
  let lastOwnerKey: string | undefined;
  const ownerKey = (o: IndexOwner | null): string => (o ? `${o.id}|${o.role}` : '');
  const ownersKey = (owner: IndexOwner | null, usOwner: IndexOwner | null): string => `${ownerKey(owner)}/${ownerKey(usOwner)}`;
  let retryN = 0;
  // A cycle that came due while the tab was hidden runs on the return.
  let deferred = false;
  // A newer cycle (owner moved mid-fetch) aborts the older fetch, and only
  // the newest cycle's completion re-arms the timer.
  let cycleSeq = 0;

  const arm = (ms: number): void => {
    if (timer != null) clearTimeout(timer);
    timer = setTimeout(() => { timer = null; void cycle(); }, ms);
  };
  const armRefresh = (): void => { retryN = 0; arm(INDEX_REFRESH_MS); };
  const armRetry = (): void => {
    arm(INDEX_RETRY_MS[Math.min(retryN, INDEX_RETRY_MS.length - 1)]);
    retryN++;
  };
  const clearWait = (): void => { if (wait != null) { clearTimeout(wait); wait = null; } };
  /** Bound the "Loading" of a first answer. On expiry the view stays muted
   *  (no longer provisional) and keeps waiting — the in-flight index or the
   *  owner poll still lands the pick. Never broad. */
  const armWait = (reason: string): void => {
    if (wait != null) return;
    wait = setTimeout(() => {
      wait = null;
      if (cancelled || state.mode !== 'muted' || state.error != null) return;
      state.error = reason;
      mute(false);
    }, INDEX_WAIT_MS);
  };
  /** Nothing to index yet: quiet, provisional while the answer is on its way. */
  const awaitFirst = (reason: string): void => {
    if (state.mode !== 'muted') mute(state.error == null);
    if (state.error == null) armWait(reason);
  };
  /** A failure: a held pick stays declared; with none the view is muted
   *  (the answer for now). Either way, retry with backoff. */
  const fail = (error: string): void => {
    clearWait();
    state.error = error;
    if (!hasPick()) mute(false);
    else publish();
    armRetry();
  };

  const cycle = async (): Promise<void> => {
    if (cancelled) return;
    // Never change scope on a hidden tab once there is an answer. The first
    // pick still proceeds.
    if (documentHidden() && (hasPick() || state.mode === 'broad')) { deferred = true; return; }
    const hadPickAtStart = hasPick();
    const seq = ++cycleSeq;
    // A missing/changing owner also invalidates an older in-flight result.
    controller?.abort();
    controller = null;
    const owner = resolveIndexOwner(sport);
    const usOwner = resolveIndexOwner(sport, 'polymarket_us');
    const capturedOwnersKey = ownersKey(owner, usOwner);
    lastOwnerKey = capturedOwnersKey;
    state.owner = owner?.id ?? null;
    publish();
    if (!owner) {
      // An owner flap keeps what is declared — a pick, or a core's whole
      // sport; the poll re-runs the moment an owner announces.
      if (hasPick() || state.mode === 'broad') return;
      awaitFirst('owner unknown');
      return;
    }
    clearWait();
    if (owner.role !== 'edge') {
      // Core- or firehose-served: the sport-wide declaration is the view.
      state.error = null;
      enter('broad', spec.broad);
      armRefresh();
      return;
    }
    if (spec.lineScope && ownerAdvertisesLineScope(owner.id)) {
      state.error = null;
      enter('lineScope', spec.lineScope);
      armRefresh();
      return;
    }
    if (spec.ready && !spec.ready()) {
      // Nothing to name yet. A held pick stays; anything else goes quiet —
      // including a core's whole sport the edge has just taken over.
      if (!hasPick()) awaitFirst('nothing to index');
      return;
    }
    const rest = engineRestFromWsUrl(owner.url);
    if (!rest) { fail('owner has no REST origin'); return; }
    controller = new AbortController();
    const { signal } = controller;
    // A first index still on the way: quiet, provisional, bounded "Loading".
    if (!hasPick() && state.mode !== 'muted') mute(true);
    if (state.mode === 'muted' && state.error == null) armWait('index slow');
    try {
      const pick = await spec.fetchPick(rest, signal);
      if (cancelled || signal.aborted || seq !== cycleSeq) return;
      if (ownersKey(resolveIndexOwner(sport), resolveIndexOwner(sport, 'polymarket_us')) !== capturedOwnersKey) { void cycle(); return; }
      if (documentHidden() && hadPickAtStart) { deferred = true; return; }
      clearWait();
      // The US lane served by the K/Intl owner itself: its names are in this
      // index, so the held separate-owner names are superseded.
      if (usOwner != null && usOwner.id === owner.id) usNames = [];
      pickNames = [...spec.pickTickers(pick)];
      state.error = null;
      state.indexedAtMs = Date.now();
      declareNames(pick);
      if (seq === cycleSeq) armRefresh();
      // A venue split: the K/Intl index cannot name US rows. Publish K/Intl
      // at once, then add the US owner's names on the same cadence.
      const usRest = spec.fetchUsTickers && usOwner && usOwner.id !== owner.id ? engineRestFromWsUrl(usOwner.url) : null;
      if (usRest && spec.fetchUsTickers) {
        try {
          const us = await spec.fetchUsTickers(usRest, pick, AbortSignal.any([signal, AbortSignal.timeout(US_INDEX_TIMEOUT_MS)]));
          if (cancelled || signal.aborted || seq !== cycleSeq) return;
          if (ownersKey(resolveIndexOwner(sport), resolveIndexOwner(sport, 'polymarket_us')) !== capturedOwnersKey) { void cycle(); return; }
          if (documentHidden() && hadPickAtStart) { deferred = true; return; }
          usNames = [...us];
          declareNames();
        } catch (e) {
          if (cancelled || signal.aborted || seq !== cycleSeq) return;
          // A US failure must not discard working K/Intl names.
          state.error = `US index: ${message(e)}`;
          publish();
        }
      }
    } catch (e) {
      if (cancelled || signal.aborted) return;
      if (seq === cycleSeq) fail(message(e));
    }
  };

  const redeclare = (): void => {
    if (cancelled) return;
    if (state.mode === 'tickers') spec.tickers(names());
    else if (state.mode === 'muted') spec.muted(mutedProvisional);
    else if (state.mode === 'broad') spec.broad();
    else if (state.mode === 'lineScope') spec.lineScope?.();
  };

  publish();
  void cycle();
  // Ownership moves (the announce lands, an edge drops, the directory
  // re-assigns): re-resolve on a slow poll and re-run when an owner changed.
  // A poll rather than a store subscription: ownership is not a store field,
  // the announce ingest does not publish a tick of its own, and the resolve
  // is a handful of set lookups.
  const poll = setInterval(() => {
    if (cancelled) return;
    if (ownersKey(resolveIndexOwner(sport), resolveIndexOwner(sport, 'polymarket_us')) !== lastOwnerKey) void cycle();
  }, OWNER_POLL_INTERVAL_MS);
  const onVisibility = (): void => {
    if (cancelled || documentHidden() || !deferred) return;
    deferred = false;
    void cycle();
  };
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);

  return {
    refresh: () => {
      if (cancelled) return;
      if (documentHidden() && (hasPick() || state.mode === 'broad')) { deferred = true; return; }
      redeclare();
      void cycle();
    },
    redeclare,
    stop: () => {
      cancelled = true;
      controller?.abort();
      clearInterval(poll);
      if (timer != null) clearTimeout(timer);
      clearWait();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}
